// src/data/shelterChangeLog.ts
// -----------------------------
// Repository for `shelter_change_log` (db-127) — the per-DFV-Service change
// history (who / what / when) shown in the History modal on ShelterDetail. Rows
// are written ONLY by the DB trigger (log_shelter_change); this repo just reads
// them (admin-only via RLS). The actor (`changed_by`) is resolved to a display
// name via a second profiles lookup (robust — no PostgREST embed dependency).
import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

export type ShelterChangeLogRow = Database["public"]["Tables"]["shelter_change_log"]["Row"];

// A single change entry, with the actor resolved to a name for display.
export interface ChangeLogEntry {
  id: string;
  sourceTable: string;
  changedAt: string;
  changedBy: string | null;
  actorName: string; // resolved display name, or "System" for a direct SQL change
  changes: Record<string, { old: unknown; new: unknown }>;
}

const COLS: string = "id, shelter_id, source_table, changed_by, changed_at, changes";

// Most-recent-first change history for a shelter. Empty array on error / no client.
export async function listShelterChangeLog(
  shelterId: string | undefined,
  limit = 200
): Promise<ChangeLogEntry[]> {
  const c = getTypedClient();
  if (!c || !shelterId) return [];
  const { data, error } = await c
    .from("shelter_change_log")
    .select(COLS)
    .eq("shelter_id", shelterId)
    .order("changed_at", { ascending: false })
    .limit(limit);
  if (error) {
    logger.warn("listShelterChangeLog failed:", error.message);
    return [];
  }
  const rows = (data as unknown as ShelterChangeLogRow[]) ?? [];

  // Resolve the distinct actor ids → display names in one query.
  const ids = [...new Set(rows.map((r) => r.changed_by).filter((v): v is string => !!v))];
  const nameById = new Map<string, string>();
  if (ids.length > 0) {
    const { data: profs, error: pErr } = await c
      .from("profiles")
      .select("id, staff_first_name, staff_last_name, staff_user_email")
      .in("id", ids);
    if (pErr) logger.warn("listShelterChangeLog actor lookup failed:", pErr.message);
    for (const p of (profs as unknown as Record<string, string | null>[]) ?? []) {
      const id = p.id as string;
      const name =
        [p.staff_first_name, p.staff_last_name].filter(Boolean).join(" ").trim() ||
        p.staff_user_email ||
        "";
      if (id) nameById.set(id, name);
    }
  }

  return rows.map((r) => ({
    id: r.id,
    sourceTable: r.source_table,
    changedAt: r.changed_at,
    changedBy: r.changed_by,
    actorName: (r.changed_by && nameById.get(r.changed_by)) || "System",
    changes: (r.changes as Record<string, { old: unknown; new: unknown }>) ?? {},
  }));
}
